import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Profile = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  website: string | null;
  is_premium: boolean;
  role: 'user' | 'admin';
  ai_credits: number;
  subscription_tier: 'free' | 'pro' | 'enterprise';
  created_at: string;
  updated_at: string;
};

export type AudioPin = {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  youtube_url: string;
  youtube_video_id: string;
  latitude: number;
  longitude: number;
  location_name: string | null;
  tags: string[];
  play_count: number;
  like_count: number;
  comment_count: number;
  is_public: boolean;
  radius: number;
  qr_code_url: string | null;
  source: string;
  ai_reason: string | null;
  color_override: string | null;
  created_at: string;
  updated_at: string;
  profiles?: Profile;
};

export type Comment = {
  id: string;
  user_id: string;
  audio_pin_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  profiles?: Profile;
};

export type Like = {
  id: string;
  user_id: string;
  audio_pin_id: string;
  created_at: string;
};

export type Follow = {
  id: string;
  follower_id: string;
  following_id: string;
  created_at: string;
};

export type Playlist = {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  profiles?: Profile;
};

export type PlaylistTrack = {
  id: string;
  playlist_id: string;
  youtube_url: string;
  title: string;
  position: number;
  created_at: string;
};

export type SoundZone = {
  id: string;
  user_id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  radius: number;
  playlist_id: string | null;
  is_active: boolean;
  color_override: string | null;
  qr_code_url: string | null;
  shape_type: 'circle' | 'polygon';
  coordinates: [number, number][] | null;
  created_at: string;
  updated_at: string;
  profiles?: Profile;
  playlists?: Playlist;
};

export type SoundTrail = {
  id: string;
  user_id: string;
  name: string;
  description: string;
  is_active: boolean;
  color_override: string | null;
  estimated_duration: number;
  difficulty_level: 'easy' | 'medium' | 'hard';
  created_at: string;
  updated_at: string;
  profiles?: Profile;
  sound_stops?: SoundStop[];
};

export type SoundStop = {
  id: string;
  trail_id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  radius: number;
  youtube_url: string;
  position: number;
  qr_code_url: string | null;
  created_at: string;
};

export type AnalyticsEvent = {
  id: string;
  user_id: string | null;
  event_type: 'drop_listen' | 'zone_enter' | 'zone_exit' | 'trail_start' | 'trail_complete' | 'stop_visit';
  content_type: 'drop' | 'zone' | 'trail' | 'stop';
  content_id: string;
  latitude: number | null;
  longitude: number | null;
  duration: number;
  metadata: Record<string, unknown>;
  created_at: string;
};
